import React, { useState, useEffect } from 'react';
import { MapPin, X } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { createCustomIcon } from '../utils/mapHelpers';
import { primaryButtonStyle, secondaryButtonStyle, applyDisabledStyle, applyIconOnlyStyle, neutralButtonStyle } from '../utils/buttonStyles';
import { 
  getSelectStyle, 
  getInputStyle, 
  getTextareaStyle, 
  labelStyle, 
  errorMessageStyle, 
  helpTextStyle,
  formContainerStyle,
  formActionsStyle,
  formGroupStyle
} from '../utils/formStyles';

// Componente para selecionar localização no mapa
const LocationSelector = ({ position, setPosition }) => {
  const map = useMapEvents({
    click: (e) => {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? (
    <Marker 
      position={position} 
      icon={createCustomIcon('Mapeada', 'fossa')}
    />
  ) : null;
};

/**
 * Componente para cadastro de fossas sépticas
 */
const CadastroFossaSeptica = ({ residenciaId, onClose, onSave }) => {
  const { 
    residencias, 
    carregarResidencias, 
    cadastrarFossa, 
    loading 
  } = useFossasSepticas();

  // Estado do formulário
  const [formData, setFormData] = useState({
    residencia_id: residenciaId || '',
    latitude: null,
    longitude: null,
    tipo: 'Convencional',
    data_instalacao: '',
    capacidade: '',
    observacoes: ''
  });

  // Estado para erros de validação
  const [errors, setErrors] = useState({});
  
  // Estado para posição no mapa
  const [position, setPosition] = useState(null);
  
  // Centro inicial do mapa (baseado na residência ou em Conceição do Castelo - ES)
  const centroConceicaoCastelo = [-20.359292, -41.247282];
  const [mapCenter, setMapCenter] = useState(centroConceicaoCastelo);
  const [zoom, setZoom] = useState(15);
  
  // Residência selecionada
  const [residencia, setResidencia] = useState(null);

  // Carregar residências ao montar o componente
  useEffect(() => {
    carregarResidencias();
  }, [carregarResidencias]);

  // Atualizar residência selecionada quando os dados forem carregados
  useEffect(() => {
    if (residenciaId && residencias.length > 0) {
      const res = residencias.find(r => r.id === parseInt(residenciaId));
      if (res) {
        setResidencia(res);
        setMapCenter([res.latitude, res.longitude]);
        setFormData(prev => ({
          ...prev,
          residencia_id: res.id
        }));
      }
    }
  }, [residenciaId, residencias]);

  // Atualizar centro do mapa quando a residência mudar
  useEffect(() => {
    if (formData.residencia_id && residencias.length > 0) {
      const res = residencias.find(r => r.id === parseInt(formData.residencia_id));
      if (res) {
        setResidencia(res);
        setMapCenter([res.latitude, res.longitude]);
      }
    }
  }, [formData.residencia_id, residencias]);

  // Atualizar estado do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Atualizar posição no mapa
  useEffect(() => {
    if (position) {
      setFormData(prev => ({
        ...prev,
        latitude: position[0],
        longitude: position[1]
      }));
    }
  }, [position]);

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.residencia_id) {
      newErrors.residencia_id = 'Residência é obrigatória';
    }
    
    if (!formData.tipo) {
      newErrors.tipo = 'Tipo de fossa é obrigatório';
    }
    
    if (!formData.latitude || !formData.longitude) {
      newErrors.location = 'Selecione a localização no mapa';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const success = await cadastrarFossa(formData);
      
      if (success && onSave) {
        onSave();
      }
    }
  };

  if (loading.residencias) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Carregando dados da residência...</p>
      </div>
    );
  }

  return (
    <div style={formContainerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderBottom: '1px solid #e9ecef' }}>
        <h2 style={{ margin: 0, fontSize: '18px' }}>Cadastrar Fossa Séptica</h2>
        <button 
          style={applyIconOnlyStyle(neutralButtonStyle)} 
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Residência</label>
            <select
              name="residencia_id"
              value={formData.residencia_id}
              onChange={handleChange}
              style={getSelectStyle({ error: !!errors.residencia_id, disabled: !!residenciaId })}
              disabled={!!residenciaId}
            >
              <option value="">Selecione uma residência</option>
              {residencias.map(res => (
                <option key={res.id} value={res.id}>
                  {res.identificacao} - {res.propriedade?.nome || 'Propriedade desconhecida'}
                </option>
              ))}
            </select>
            {errors.residencia_id && <div style={errorMessageStyle}>{errors.residencia_id}</div>}
          </div>
          
          <div style={formGroupStyle}>
            <label style={labelStyle}>Tipo de Fossa</label>
            <select
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              style={getSelectStyle({ error: !!errors.tipo })}
            >
              <option value="Convencional">Fossa Séptica Convencional</option>
              <option value="Biodigestora">Fossa Biodigestora</option>
              <option value="Ecologica">Fossa Ecológica</option>
              <option value="Sumidouro">Sumidouro</option>
            </select>
            {errors.tipo && <div style={errorMessageStyle}>{errors.tipo}</div>}
          </div>
          
          <div style={formGroupStyle}>
            <label style={labelStyle}>Data de Instalação (opcional)</label>
            <input
              type="date"
              name="data_instalacao"
              value={formData.data_instalacao}
              onChange={handleChange}
              style={getInputStyle()}
            />
          </div>
          
          <div style={formGroupStyle}>
            <label style={labelStyle}>Capacidade (opcional)</label>
            <input
              type="text"
              name="capacidade"
              value={formData.capacidade}
              onChange={handleChange}
              style={getInputStyle()}
              placeholder="Ex: 1000 litros"
            />
          </div>
        </div>
        
        <div style={formGroupStyle}>
          <label style={labelStyle}>Observações (opcional)</label>
          <textarea
            name="observacoes"
            value={formData.observacoes}
            onChange={handleChange}
            style={getTextareaStyle()}
            placeholder="Informações adicionais sobre a fossa séptica"
          />
        </div>
        
        {mapCenter && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ 
              display: 'flex', 
              alignItems: 'center',
              fontSize: '16px',
              marginBottom: '10px'
            }}>
              <MapPin size={16} style={{ marginRight: '5px' }} />
              Localização da Fossa Séptica
            </h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
              Clique no mapa para selecionar a localização exata da fossa séptica.
            </p>
            
            {errors.location && <div style={{ 
              ...errorMessageStyle,
              marginBottom: '10px',
              padding: '8px 12px',
              backgroundColor: '#f8d7da',
              borderRadius: '4px'
            }}>{errors.location}</div>}
            
            <div style={{ 
              height: '300px',
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '10px',
              border: '1px solid #ced4da'
            }}>
              <MapContainer 
                center={mapCenter} 
                zoom={zoom} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {residencia && (
                  <Marker 
                    position={[residencia.latitude, residencia.longitude]} 
                    icon={createCustomIcon(residencia.status, 'residencia')}
                  />
                )}
                <LocationSelector position={position} setPosition={setPosition} />
              </MapContainer>
            </div>
            
            {position && (
              <div style={{ 
                padding: '10px 15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <MapPin size={16} style={{ marginRight: '8px', color: '#2e7d32' }} />
                <strong>Coordenadas:</strong>&nbsp;{position[0].toFixed(6)}, {position[1].toFixed(6)}
              </div>
            )}
          </div>
        )}
        
        <div style={formActionsStyle}>
          <button 
            type="button" 
            onClick={onClose}
            style={secondaryButtonStyle}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            disabled={loading.fossas}
            style={applyDisabledStyle(primaryButtonStyle, loading.fossas)}
          >
            {loading.fossas ? 'Salvando...' : 'Cadastrar'}
          </button>
        </div>
      </form>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .map-container {
          height: 300px;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 10px;
        }
      `}</style>
    </div>
  );
};

export default CadastroFossaSeptica;